/** Full-screen Google sign-in. Shown when there's no valid session. */
import { useEffect, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { useAuthStore } from '../../state/authStore.ts';
import { toast } from '../../state/toastStore.ts';

export function SignIn() {
  const setToken = useAuthStore((s) => s.setToken);
  const focusRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    focusRef.current?.focus();
  }, []);

  return (
    <div className="flex min-h-screen-dvh flex-col items-center justify-center p-6">
      <div
        ref={focusRef}
        tabIndex={-1}
        className="w-full max-w-sm rounded-2xl border border-line bg-surface p-8 text-center shadow-md outline-none"
      >
        <div className="mx-auto mb-4 grid h-14 w-14 place-items-center overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-line">
          <img src="/logo.png" alt="" className="h-full w-full object-contain p-1" />
        </div>
        <h1 className="font-display text-2xl font-bold tracking-tight text-fg">Bhandari Photos</h1>
        <p className="mt-1 text-[0.6875rem] font-medium uppercase tracking-wider text-muted">
          Memories • Moments • Forever
        </p>
        <p className="mt-5 text-sm text-fg-2">Sign in to view and share family photos.</p>

        <div className="mt-6 flex justify-center">
          <GoogleLogin
            onSuccess={(cred) => {
              if (cred.credential) setToken(cred.credential);
              else toast.error('Sign-in failed — no credential returned');
            }}
            onError={() => toast.error('Google sign-in failed')}
            // One Tap + auto_select silently re-issue a fresh token when the
            // user is still signed into Google. Enabled everywhere; the FedCM
            // flags below are what make this actually work on iOS
            // (Chrome iOS 108+ / Safari 17+ ship the FedCM API that Google's
            // silent flow uses in place of third-party cookies).
            useOneTap
            auto_select
            use_fedcm_for_prompt
            use_fedcm_for_button
            theme="filled_blue"
            shape="pill"
            text="signin_with"
          />
        </div>
      </div>

      {/* Public legal links — required on the home page for Google OAuth verification. */}
      <div className="mt-6 flex items-center justify-center gap-4 text-xs text-muted">
        <NavLink to="/privacy" className="hover:text-fg-2">
          Privacy Policy
        </NavLink>
        <NavLink to="/terms" className="hover:text-fg-2">
          Terms of Service
        </NavLink>
      </div>
    </div>
  );
}
